import React, { Fragment, useState, useEffect } from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import { Form, Button, Row, Col } from "react-bootstrap";
import { updateDb } from "../services/updateDb";
import { useTranslation } from "react-i18next";
import {  useNavigate } from "react-router-dom";
import { Options } from "../risks/iso-verklaringen";
import { useAppContext } from "../AppContext";
import { expandCategories, getCategoryValue } from "../common/helpers";

const RiskTemplateForm = ({ risks, setRisks, riskTemplateData, templateId}) => {
    const { t, i18n } = useTranslation();
    const { currentOrg } = useAppContext();
    const navigate = useNavigate();
    const [measures, setMeasures] = useState([[]]);
    const [securityStandard, setSecurityStandard] = useState(currentOrg.securityStandard);

    useEffect(() => {
        if (riskTemplateData) {
            const lang = i18n.language;
            const std = riskTemplateData.securityStandard || currentOrg.securityStandard;
            setMeasures(riskTemplateData.risks.map((risk) => {
                // Check if the measures array is empty
                if (!risk.measures || risk.measures.length === 0) {
                    return [];
                }
                // Expand category IDs from DB format to {value, label} for Typeahead
                return risk.measures.map(m => ({
                    ...m,
                    category: expandCategories(m.category, lang, std)
                }));
            }));

            if(riskTemplateData.securityStandard) 
                setSecurityStandard(riskTemplateData.securityStandard);
        }
        else {
            setMeasures([[{ measure: "", category: [] }]]);
        }
    }, [riskTemplateData]);

    const updateSecurityStandard = (e) => {
        setSecurityStandard(e.target.value);
    };

    const handleMeasureChange = (i, e, riskIndex) => {        
        let newMeasures = [...measures];

        if (!e.target)
            newMeasures[riskIndex][i]["category"] = e;
        else if (e.target.type === "text") // old behavior for measures
            newMeasures[riskIndex][i]["measure"] = e.target.value;

        setMeasures(newMeasures);
    };

    const addMeasureFields = (riskIndex) => {
        let newMeasures = [...measures];
        newMeasures[riskIndex].push({ measure: "", category: [] });
        setMeasures(newMeasures);
        
    };

    const removeMeasureFields = (i,riskIndex) => {
        let newMeasures = [...measures];
        newMeasures[riskIndex].splice(i, 1);
        setMeasures(newMeasures);
    };


    const submitForm = async (e) => {
        e.preventDefault();
    
        const formData = new FormData(e.target);

        // add measures to risks, stripping derived fields not needed by the server
        const updatedRisks = risks.map((risk, i) => ({
            measures: measures[i]?.map(m => ({
                ...m,
                category: m.category?.map(cat => getCategoryValue(cat))
            })),
            ...risk
        }));

        let templateObj = {
            name: formData.get('name'),
            securityStandard: securityStandard,
            risks: updatedRisks,
        };

        if(riskTemplateData) {
            const res = await updateDb("/api/risktemplates/update/" + templateId, templateObj);

            if (res.success) 
                navigate("/risktemplates", { state: { message: t('risk_template') + " " + t('updated'), type: "warning" } });
        }
        else {
            const res = await updateDb("/api/risktemplates/", templateObj);

            if (res.success) 
                navigate("/risktemplates", { state: { message: t('risk_template') + " " + t('added'), type: "success" } });
        }
    };

    let handleChange = (i, e, field) => {
        let newRisks = [...risks];

        if(field === 'description') 
            newRisks[i][field] = e.target.value;
        else 
            newRisks[i][field] = e.target.checked;

        setRisks(newRisks);
    };

    let addRiskFields = () => {
        setRisks([...risks, { description: "", availability: false, integrity: false, confidentiality: false }]);
        setMeasures([...measures, [{ measure: "", category: [] }]]);
    };

    let removeRiskFields = (i) => {
        let newRisks = [...risks];
        newRisks.splice(i, 1);
        setRisks(newRisks);
    };

    return (
    <Form onSubmit={submitForm} id="addRiskTemplate">

        <Row className="mb-3">

            <Form.Group as={Col} >
                <Form.Label><span className="blue-bg">{t('risk_template')} {t('name')}</span></Form.Label>
                <Form.Control
                    as="input"
                    name="name"
                    defaultValue={riskTemplateData ? riskTemplateData.name : ""}
                    placeholder={t('risk_template_name_placeholder')}
                    required
                />
            </Form.Group>
        </Row>

        <Row className="mb-3">

        <Form.Group as={Col} >
            <Form.Label><span className="blue-bg">{t('security_standard')}</span></Form.Label>
            <Form.Select name="securityStandard" value={securityStandard} onChange={updateSecurityStandard}>
                <option value="ISO27001">ISO27001</option>
                <option value="NEN7510">NEN7510</option>
                <option value="ISO42001">ISO42001</option>
            </Form.Select>
        </Form.Group>
        </Row>

        <fieldset className="border mb-3">

        <Button className="add-btn" type="button" onClick={addRiskFields}>+</Button>

        <legend className="float-none w-auto">{t('risks_plural')}</legend>

        {risks.map((el, riskIndex) => (
        <Fragment key={"risks" + riskIndex}>
            <fieldset className="border mb-3">
            <legend className="float-none w-auto legend-blue">#{riskIndex+1}</legend>
            <Row className="mb-3 align-items-center">

                <Form.Group as={Col} lg={9} controlId={"description-" + riskIndex}>
                    <Form.Label><span className="blue-bg">{t('risk_description')}</span></Form.Label>
                    <Form.Control as="textarea" rows={3} name="description" placeholder={t('risk_template_description_placeholder')} value={el.description} onChange={(e) => handleChange(riskIndex, e, 'description')} required />
                </Form.Group>

                <Form.Group as={Col} style={{position: "relative"}}>

                    <Form.Label><span className="blue-bg">{t('cia').toUpperCase()}</span></Form.Label>

                    <Form.Check
                    type="checkbox"
                    id="availabilityCheck"
                    name="availability"
                    label={t('availability')}
                    onChange={(e) => handleChange(riskIndex, e, 'availability')}
                    defaultChecked={el.availability ? true : false}
                    />

                    <Form.Check
                    type="checkbox"
                    id="integrityCheck"
                    name="integrity"
                    label={t('integrity')}
                    onChange={(e) => handleChange(riskIndex, e, 'integrity')}
                    defaultChecked={el.integrity ? true : false}
                    />

                    <Form.Check
                    type="checkbox"
                    id="confidentialityCheck"
                    name="confidentiality"
                    label={t('confidentiality')}
                    onChange={(e) => handleChange(riskIndex, e, 'confidentiality')}
                    defaultChecked={el.confidentiality ? true : false}

                    />

                    <i className="icon-delete remove-template"  onClick={() => removeRiskFields(riskIndex)} />
                </Form.Group>


        
            </Row>

                

            <fieldset className="border mb-3">
            <Button className="add-btn" variant="primary" type="button" onClick={() => addMeasureFields(riskIndex)}>
                +
            </Button>

            <legend className="float-none w-auto">{t('measures')}</legend>
            {measures[riskIndex]?.map((el, mIndex) => (
                <Row className="mb-3" key={"risk-" + riskIndex + " _ " + mIndex}>
                <Form.Group as={Col} controlId={"measure-" + riskIndex + " _ " + mIndex}>
                    
                    <Form.Control
                        placeholder={t('measure')}
                        value={el.measure}
                        onChange={(e) => handleMeasureChange(mIndex, e, riskIndex)}
                        
                    />
                

                </Form.Group>

                <Form.Group as={Col} controlId={"measurecat-" + riskIndex + " _ " + mIndex}>
                    <Typeahead
                    id={"basic-typeahead-multiple-" + riskIndex + " _ " + mIndex}
                    labelKey="label"
                    multiple
                    onChange={(e) => handleMeasureChange(mIndex, e, riskIndex)}
                    options={Options(i18n.language, securityStandard).filter(
                        opt => !(measures[riskIndex][mIndex].category || []).some(sel => getCategoryValue(sel) === opt.value)
                    )}
                    placeholder={t('category_placeholder')}
                    selected={measures[riskIndex][mIndex].category}
                    name="category"
                    />
                </Form.Group>


                <Form.Group as={Col} xs="auto">
                    <i className="icon-delete"  style={{lineHeight: '38px'}} onClick={() => removeMeasureFields(mIndex, riskIndex)} />
                </Form.Group>
                </Row>
            ))}
            </fieldset>
            </fieldset>

        </Fragment>
        ))}

        </fieldset>

        <div className="vertical-spacer" />

        <input className="white-border-btn submit-btn" type="submit" value={riskTemplateData ? t('edit_risk_template') : t('add_risk_template')} />
            
    </Form>
);
};
export default RiskTemplateForm;
